/**
 * `{{variable}}` interpolation for prompt templates.
 *
 * Supported slot syntax: `{{ name }}`, `{{user_query}}`, `{{context_json}}`.
 * Variable names are JS/Python-friendly identifiers (letters, digits, `_`,
 * may contain `.` / `-` for namespacing such as `user.locale`).
 */

export const VARIABLE_PATTERN = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g;

/** Unique variables in order of first appearance. */
export function extractVariables(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  if (!text) return out;
  for (const match of text.matchAll(VARIABLE_PATTERN)) {
    const name = match[1];
    if (!seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  }
  return out;
}

export interface InterpolationResult {
  output: string;
  /** Variables present in the template but missing from the provided values. */
  missing: string[];
}

/**
 * Replace every `{{slot}}` with its value.
 * Missing values resolve to an empty string and are reported in `missing`,
 * so a run never silently ships a literal `{{placeholder}}` to a model.
 */
export function interpolate(text: string, values: Record<string, string> = {}): InterpolationResult {
  const missing = new Set<string>();
  const output = text.replace(VARIABLE_PATTERN, (_full, name: string) => {
    const raw = values[name];
    if (raw === undefined || raw === null) {
      missing.add(name);
      return '';
    }
    return String(raw);
  });
  return { output, missing: [...missing] };
}

export function hasVariables(text: string): boolean {
  VARIABLE_PATTERN.lastIndex = 0;
  return VARIABLE_PATTERN.test(text);
}

/** Fill the editor preview: unresolved slots render as `‹name›` for visibility. */
export function interpolatePreview(text: string, values: Record<string, string> = {}): InterpolationResult {
  const missing = new Set<string>();
  const output = text.replace(VARIABLE_PATTERN, (_full, name: string) => {
    const raw = values[name];
    if (raw === undefined || raw === null || raw === '') {
      missing.add(name);
      return `‹${name}›`;
    }
    return String(raw);
  });
  return { output, missing: [...missing] };
}
